import { useEffect, useMemo, useState } from "react";
import { AuthTokenRetrieve, LoginButton } from "../Utility/AuthTokenRetrieve";
import { buildApiUrl } from "../Utility/api";
import "../Food/RecipeList.css";
import "./FoodDiary.css";
import {
  defaultMealForm,
  fetchDiaryMeals,
  fetchRecipes,
  parseManualIngredients,
} from "./FoodDiaryShared";

const FoodDiaryAddMeal = () => {
  const [shouldShow, setShouldShow] = useState();
  const [recipes, setRecipes] = useState([]);
  const [mealForm, setMealForm] = useState(defaultMealForm());
  const [savingMeal, setSavingMeal] = useState(false);
  const [mealStatus, setMealStatus] = useState("");
  const [selectedMode, setSelectedMode] = useState("recipe");
  const [recipeSearchText, setRecipeSearchText] = useState("");
  const [recipeTypeFilter, setRecipeTypeFilter] = useState("All");
  const { shouldShow: shouldShowGoogle } = AuthTokenRetrieve();

  useEffect(() => {
    setShouldShow(shouldShowGoogle);
  }, [shouldShowGoogle]);

  const accessToken = localStorage.getItem("lesterAccessToken") || "";

  useEffect(() => {
    if (!shouldShow || !accessToken) {
      return;
    }

    fetchRecipes().then((recipeData) => {
      setRecipes(recipeData || []);
    });
  }, [shouldShow, accessToken]);

  const recipeOptions = useMemo(() => {
    return [...recipes].sort((leftValue, rightValue) =>
      leftValue.Name.localeCompare(rightValue.Name)
    );
  }, [recipes]);

  const recipeTypes = useMemo(() => {
    const uniqueTypes = new Set(
      recipes
        .map((recipe) => recipe.Type)
        .filter(Boolean)
    );
    return ["All", ...Array.from(uniqueTypes).sort()];
  }, [recipes]);

  const filteredRecipeOptions = useMemo(() => {
    return recipeOptions
      .filter((recipe) => recipeTypeFilter === "All" || recipe.Type === recipeTypeFilter)
      .filter((recipe) =>
        !recipeSearchText ||
        recipe.Name.toLowerCase().includes(recipeSearchText.toLowerCase())
      )
      .slice(0, 12);
  }, [recipeOptions, recipeSearchText, recipeTypeFilter]);

  const handleMealChange = (event) => {
    const { name, value } = event.target;
    setMealForm((currentValue) => ({ ...currentValue, [name]: value }));
  };

  const selectRecipe = (recipe) => {
    setMealForm((currentValue) => ({ ...currentValue, recipeId: String(recipe.Id) }));
    setRecipeSearchText(recipe.Name);
  };

  const createMeal = async () => {
    const response = await fetch(buildApiUrl("/food/diary/meals"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        accessToken,
        eatenAt: mealForm.eatenAt,
        mealType: mealForm.mealType,
        notes: mealForm.notes,
      }),
    });
    return response.json();
  };

  const addRecipeItem = async (mealId) => {
    const response = await fetch(buildApiUrl(`/food/diary/meals/${mealId}/recipe-item`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        accessToken,
        recipeId: Number(mealForm.recipeId),
        servingsEaten: Number(mealForm.servingsEaten || 1),
      }),
    });
    return response.json();
  };

  const addManualItem = async (mealId) => {
    const response = await fetch(buildApiUrl(`/food/diary/meals/${mealId}/manual-item`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        accessToken,
        displayName: mealForm.manualItemName,
        servingsEaten: Number(mealForm.servingsEaten || 1),
        ingredients: parseManualIngredients(mealForm.manualIngredientText),
      }),
    });
    return response.json();
  };

  const handleMealSubmit = async (event) => {
    event.preventDefault();
    setSavingMeal(true);
    setMealStatus("");

    try {
      if (selectedMode === "recipe" && !mealForm.recipeId) {
        setMealStatus("Choose a recipe before logging the meal.");
        setSavingMeal(false);
        return;
      }

      if (selectedMode === "manual" && !mealForm.manualItemName.trim()) {
        setMealStatus("Add a manual item name before saving.");
        setSavingMeal(false);
        return;
      }

      const mealResponse = await createMeal();
      if (!mealResponse.meal?.id) {
        throw new Error("Meal save failed");
      }

      if (selectedMode === "recipe") {
        await addRecipeItem(mealResponse.meal.id);
      } else {
        await addManualItem(mealResponse.meal.id);
      }

      await fetchDiaryMeals(accessToken);
      setMealForm(defaultMealForm());
      setRecipeSearchText("");
      setRecipeTypeFilter("All");
      setMealStatus("Meal logged");
    } catch (err) {
      setMealStatus(err.message ? err.message : "Unable to save meal");
    }

    setSavingMeal(false);
  };

  if (!shouldShow) {
    return (
      <div className="searchBarDiv formViewWrapper FoodDiaryLogin">
        <h2>Food diary</h2>
        <p>Sign in to add private meal entries.</p>
        <LoginButton setShouldShow={setShouldShow}></LoginButton>
      </div>
    );
  }

  return (
    <div className="searchBarDiv formViewWrapper FoodDiaryPanel FoodDiarySinglePanel">
      <h2 className="FoodDiaryTitle">Add meal</h2>
      <p className="FoodDiarySubtitle">
        Log a meal from a saved recipe or a quick manual entry.
      </p>
      <form onSubmit={handleMealSubmit}>
        <div className="FoodDiaryTwoColumn">
          <div className="inputDiv">
            <label>When eaten</label>
            <input
              type="datetime-local"
              className="inputBox addInput"
              name="eatenAt"
              value={mealForm.eatenAt}
              onChange={handleMealChange}
            />
          </div>
          <div className="inputDiv">
            <label>Meal type</label>
            <select
              className="inputBox addInput"
              name="mealType"
              value={mealForm.mealType}
              onChange={handleMealChange}
            >
              <option>Breakfast</option>
              <option>Lunch</option>
              <option>Dinner</option>
              <option>Snack</option>
            </select>
          </div>
        </div>

        <div className="FoodDiaryModeRow">
          <button
            type="button"
            className={`summaryButton ${selectedMode === "recipe" ? "isSelected" : ""}`}
            onClick={() => setSelectedMode("recipe")}
          >
            Recipe
          </button>
          <button
            type="button"
            className={`summaryButton ${selectedMode === "manual" ? "isSelected" : ""}`}
            onClick={() => setSelectedMode("manual")}
          >
            Manual
          </button>
        </div>

        {selectedMode === "recipe" ? (
          <>
            <div className="FoodDiaryTwoColumn">
              <div className="inputDiv">
                <label>Recipe type</label>
                <select
                  className="inputBox addInput"
                  value={recipeTypeFilter}
                  onChange={(event) => setRecipeTypeFilter(event.target.value)}
                >
                  {recipeTypes.map((typeValue) => (
                    <option key={typeValue} value={typeValue}>
                      {typeValue}
                    </option>
                  ))}
                </select>
              </div>
              <div className="inputDiv">
                <label>Search recipe</label>
                <input
                  className="inputBox addInput"
                  value={recipeSearchText}
                  onChange={(event) => {
                    setRecipeSearchText(event.target.value);
                    setMealForm((currentValue) => ({ ...currentValue, recipeId: "" }));
                  }}
                  placeholder="Start typing a recipe name"
                />
              </div>
            </div>

            <div className="inputDiv">
              <label>Matching recipes</label>
              <div className="FoodDiaryRecipeResults">
                {filteredRecipeOptions.length ? (
                  filteredRecipeOptions.map((recipe) => (
                    <button
                      type="button"
                      key={recipe.Id || recipe.Name}
                      className={`FoodDiaryRecipeResult ${String(recipe.Id) === mealForm.recipeId ? "FoodDiaryRecipeResultSelected" : ""}`}
                      onClick={() => selectRecipe(recipe)}
                    >
                      <span>{recipe.Name}</span>
                      <span className="FoodDiaryRecipeType">{recipe.Type || "Uncategorized"}</span>
                    </button>
                  ))
                ) : (
                  <div className="FoodDiaryRecipeEmpty">No recipes match the current search.</div>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="inputDiv">
              <label>Manual item name</label>
              <input
                className="inputBox addInput"
                name="manualItemName"
                value={mealForm.manualItemName}
                onChange={handleMealChange}
              />
            </div>
            <div className="inputDiv">
              <label>Ingredients, one per line</label>
              <textarea
                className="inputBox textAreaAdd FoodDiaryTextarea"
                name="manualIngredientText"
                value={mealForm.manualIngredientText}
                onChange={handleMealChange}
              />
            </div>
          </>
        )}

        <div className="FoodDiaryTwoColumn">
          <div className="inputDiv">
            <label>Servings eaten</label>
            <input
              className="inputBox addInput"
              name="servingsEaten"
              value={mealForm.servingsEaten}
              onChange={handleMealChange}
            />
          </div>
          <div className="inputDiv">
            <label>Meal notes</label>
            <input
              className="inputBox addInput"
              name="notes"
              value={mealForm.notes}
              onChange={handleMealChange}
            />
          </div>
        </div>

        {mealStatus ? <p className="FoodDiaryStatus">{mealStatus}</p> : null}
        <button type="submit" className="summaryButton" disabled={savingMeal}>
          {savingMeal ? "Saving..." : "Save Meal"}
        </button>
      </form>
    </div>
  );
};

export default FoodDiaryAddMeal;
